import React, { useContext, useEffect, useState } from 'react';
import { useQuery, type QueryObserverBaseResult } from '@tanstack/react-query';
import { useDeepCompareEffect, useLoginInfo } from '@fuxi/eevee-hooks';
import type { RbacUserInfo } from '@fuxi/eevee-hooks/dist/hooks/useLoginInfo';
import { ExportVersion, ExportVersionStatus, TaskRoleDetail, StageStatus, Stage, TaskStatus } from 'common/types';
import useAntStaticComponent from 'common/hooks/useAntStaticComponent';
import service from '@/service';
import { getParentTaskRoleInfo } from '@/utils';
import { useUserAccount } from '@/hooks';

type Props = { taskId: number; isParent: boolean; refetchFlag?: any; disableQuery?: boolean };

export type TaskDetail = Stage & {
  parentId: number;
  isTaskOver: boolean;
  isParentNotProcessing: boolean;
  isTaskLoading: boolean;
  isVersionListLoading: boolean;
  exportVersions: ExportVersion[];
  roleInfo?: TaskRoleDetail;
  rbacUserInfo: RbacUserInfo;
  isTodo: boolean;
  ugcGameAndType: { gameId: number; type: string };
  refetchTask: QueryObserverBaseResult['refetch'];
  refetchVersionList: QueryObserverBaseResult['refetch'];
};

const NO_PROVIDER = {};
const TaskDetailContext = React.createContext(NO_PROVIDER);

const TaskDetailProvider: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const { taskId, refetchFlag, disableQuery, children, isParent } = props;
  const { message } = useAntStaticComponent();
  const { rbacUserInfo } = useLoginInfo();
  const { platformPermission } = useUserAccount();
  const [versionDetail, setVersionDetail] = useState<Partial<TaskDetail>>({
    isTaskLoading: true,
    isVersionListLoading: true,
  });

  const {
    isLoading: isTaskLoading,
    isError: getTaskIsError,
    data: task,
    refetch: refetchTask,
  } = useQuery(
    ['getTaskInfo', taskId],
    async () => {
      const task = await service.tasks.getTaskInfo({ taskId });
      let projectPermission = {};

      if (task?.projectId) {
        const res = await service.userSetting.getProjectPermission({ projectId: task?.projectId });
        projectPermission = res.data?.[task?.projectId];
      }

      let ugcGameAndType = { gameId: -1, type: '' };

      if (task?.projectExtra) {
        try {
          const obj = JSON.parse(task.projectExtra);

          ugcGameAndType = {
            gameId: obj.gameId,
            type: obj.type,
          };
        } catch (e) {
          console.error(e);
        }
      }
      console.log('projectPermission', projectPermission);
      console.log('ugcGameAndType', ugcGameAndType);
      return { ...task, projectPermission, ugcGameAndType };
    },
    {
      enabled: !isParent && !!taskId && !disableQuery,
    },
  );

  const {
    isLoading: isVersionListLoading,
    isError: getVersionListIsError,
    data: exportVersions,
    refetch: refetchVersionList,
  } = useQuery(
    ['getVersionList', taskId],
    async () => {
      const data = await service.exportVersions.getVersionList(taskId, ExportVersionStatus.AllStatus);
      if (Array.isArray(data)) {
        return data;
      } else {
        return [];
      }
    },
    {
      enabled: !isParent && !!taskId && !disableQuery,
    },
  );

  const refetch = () => {
    refetchTask();
    refetchVersionList();
  };

  useEffect(() => {
    if (refetchFlag) {
      refetchTask();
      refetchVersionList();
    }
  }, [refetchFlag, refetchTask, refetchVersionList]);

  useDeepCompareEffect(() => {
    if (taskId) {
      const roles = task?.role;
      const roleInfo = getParentTaskRoleInfo({ roles, platformPermission, projectPermission: task?.projectPermission });

      setVersionDetail({
        ...versionDetail,
        ...task,
        isTaskLoading,
        isVersionListLoading,
        isTaskOver: task?.status === StageStatus.Pass,
        isParentNotProcessing: task?.parentTaskStatus !== TaskStatus.Processing,
        roleInfo,
        exportVersions,
        rbacUserInfo,
      });
    }
  }, [task, exportVersions, taskId]);

  if (getTaskIsError) {
    message.error(`获取需求详情信息失败，需求 ID： ${taskId}`);
  }

  if (getVersionListIsError) {
    message.error(`获取需求版本，需求 ID： ${taskId}`);
  }

  return (
    <TaskDetailContext.Provider value={{ ...versionDetail, refetch, taskId }}>{children}</TaskDetailContext.Provider>
  );
};

const useTaskDetailContext = () => {
  const value = useContext(TaskDetailContext);

  if (value === NO_PROVIDER) {
    // eslint-disable-next-line no-console
    console.warn(`useTaskDetailContext must be wrapped with TaskDetailProvider`);
  }

  return value as TaskDetail;
};

export { TaskDetailProvider, useTaskDetailContext };
